import { status } from '@sportninja/common/constants/attendanceStatus'
import { ATTENDANCE_STATUS_LABELS } from '@sportninja/common/features/game/attendance/constants'

// Single source for the attendance status copy shared by the help modal and the
// row tooltips. Mirrors the mobile AttendanceHelpBottomSheet statuses/copy.
export interface AttendanceStatusCopy {
  title: string
  description: string
  // Shorter wording for the row tooltip, where the longer help-modal sentence
  // reads as too much. Falls back to `description` when absent.
  tooltipDescription?: string
}

export const ATTENDANCE_STATUS_COPY: Record<string, AttendanceStatusCopy> = {
  not_attending: {
    title: 'Not Available',
    description: 'Player is not available for the game/event',
  },
  maybe_attending: {
    title: 'Maybe',
    description: 'Player hasn’t decided yet',
  },
  attending: {
    title: 'Available',
    description: 'Player is available for the game/event',
  },
  injury: {
    title: 'Injury',
    description: 'Player is injured and not available',
    tooltipDescription: 'Player is injured',
  },
  suspended: {
    title: 'Suspended',
    description: 'Player is suspended from the game/event',
  },
}

// Roster section availability tooltips (SN-8476): rostered players keep the
// attending wording. Spares and the help modal use ATTENDANCE_STATUS_COPY.
const ROSTER_LABELS = ATTENDANCE_STATUS_LABELS.roster
export const ROSTER_ATTENDANCE_TOOLTIP_BY_VALUE: Record<number, string> = {
  [status.ATTENDING]: `${ROSTER_LABELS.attending} — Player is attending the game`,
  [status.MAYBE]: `${ROSTER_LABELS.maybe} — Player hasn’t decided yet`,
  [status.NOT_ATTENDING]: `${ROSTER_LABELS.notAttending} — Player is not attending the game`,
}

// Spare action tooltips. Promote/withdraw sit on the Eligible Game Spares
// plus/minus icons; demote is reused by the minus icon and by the Roster Spares
// CONFIRM checkbox, both of which reverse an already-granted promotion.
export const PROMOTE_TOOLTIP = 'Promote — Add player to the team roster'
export const WITHDRAW_TOOLTIP = 'Withdraw — Cancel the request'
export const DEMOTE_TOOLTIP = 'Demote — Remove player from the team roster'

// Roster Spares availability tooltips while the spare is not yet confirmed for
// the game (SN-8476): their answer alone doesn't put them in it. Once confirmed
// they fall back to the regular status tooltips.
export const UNCONFIRMED_ROSTER_SPARE_TOOLTIP_BY_VALUE: Record<number, string> =
  {
    [status.ATTENDING]: 'Available — Must be confirmed to play in this game',
    [status.NOT_ATTENDING]:
      'Not Available — Won’t be confirmed to play in this game',
  }

// Column header tooltips (SN-8476).
export const ATTENDING_HEADER_TOOLTIP =
  'Whether each player says they’ll attend this game'
export const AVAILABLE_HEADER_TOOLTIP =
  'Whether each spare says they’re available for this game'
export const CONFIRM_ATTENDING_HEADER_TOOLTIP =
  'A team official confirms which spares can play in this game. Only confirmed spares count toward Yes.'

// Starting Keeper / Starting Goalie column header: picking here PROPOSES the
// starter (is_proposed_starting) — one per team.
export const getStartingHeaderTooltip = (isSoccer: boolean): string =>
  `Propose this game’s starting ${
    isSoccer ? 'keeper' : 'goalie'
  } — one per team`

// Roster Spares CONFIRM ATTENDING checkbox tooltips.
export const CONFIRM_TOOLTIP = 'Confirm — this spare can play in this game'
export const UNCONFIRM_TOOLTIP =
  'Unconfirm — this spare can’t play in this game'
export const CONFIRM_LOCKED_WAIVER_TOOLTIP =
  'Waiver not signed — can’t be confirmed'

// Jersey number cell tooltips.
export const JERSEY_EDIT_TOOLTIP = 'Edit Jersey Number'
export const JERSEY_LOCKED_SUSPENDED_TOOLTIP =
  'Suspended players can’t change their jersey number'
export const JERSEY_LOCKED_WAIVER_TOOLTIP =
  'Waiver not signed — jersey number is locked'

export const getJerseyConflictTooltip = (names: string[]): string =>
  names.length > 0
    ? `Same as ${names.join(', ')} — click to change`
    : 'Duplicate jersey number — click to change'

// Maps an availability icon value to its copy key.
export const ATTENDANCE_STATUS_COPY_KEY_BY_VALUE: Record<number, string> = {
  [status.ATTENDING]: 'attending',
  [status.MAYBE]: 'maybe_attending',
  [status.NOT_ATTENDING]: 'not_attending',
}

export const getAttendanceStatusTooltip = (key: string): string => {
  const copy = ATTENDANCE_STATUS_COPY[key]
  if (!copy) {
    return ''
  }
  return `${copy.title} — ${copy.tooltipDescription ?? copy.description}`
}
